import { useState } from "react";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { Button, InputSelect } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { useAvailabilityTierList } from "../../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { useAvailabilityTierGetStats } from "../../../hooks/api/v2/availabilityTier/useAvailabilityTierGetStats";
import {
  AvailabilityTierGetStatsResponseType,
  AvailabilityTierListResponseType,
} from "@sellar-io/api-v2/types";
import { useForm, useWatch } from "react-hook-form";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useAvailabilityTierDeleteAndReassign } from "../../../hooks/api/v2/availabilityTier/useAvailabilityDeleteAndReassign";
import { useAvailabilityTierSetDefault } from "../../../hooks/api/v2/availabilityTier/useAvailabilityTierSetDefault";

type AvailabilityTierDeleteDialogProps = {
  availabilityTierId: number;
  availabilityTierName: string;
  isDefault: boolean;
  active: boolean;
  setActive: (active: boolean) => void;
};

export const AvailabilityTierDeleteDialog = ({
  availabilityTierId,
  availabilityTierName,
  isDefault,
  active,
  setActive,
}: AvailabilityTierDeleteDialogProps) => {
  const availabilityTierStatsQuery = useAvailabilityTierGetStats(
    availabilityTierId,
    {
      enabled: Boolean(availabilityTierId),
    },
  );

  const customersDirectlyAssignedCount =
    availabilityTierStatsQuery.data?.customersDirectlyAssignedCount ?? 0;
  const customerZonesAssigned =
    availabilityTierStatsQuery.data?.customerZonesAssigned ?? [];

  const hasEntitiesToReassign =
    customersDirectlyAssignedCount > 0 || customerZonesAssigned.length > 0;

  const description = hasEntitiesToReassign
    ? "To delete this product catalogue, you will need to re-assign a different product catalogue to the individual customers and/or customer zones that currently have it assigned"
    : `Are you sure you want to delete ${availabilityTierName} product catalogue`;

  return (
    <>
      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActive(false)}
          title={`Delete ${availabilityTierName}?`}
          description={description}
        />
        <DialogContent>
          {active && (
            <Content
              availabilityTierId={availabilityTierId}
              isDefault={isDefault}
              setActive={setActive}
              hasEntitiesToReassign={hasEntitiesToReassign}
              customersDirectlyAssignedCount={customersDirectlyAssignedCount}
              customerZonesAssigned={customerZonesAssigned}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Content = ({
  availabilityTierId,
  isDefault,
  setActive,
  hasEntitiesToReassign,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
}: {
  availabilityTierId: number;
  isDefault: boolean;
  setActive: (active: boolean) => void;
  hasEntitiesToReassign: boolean;
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: {
    id: number;
    name: string | null;
    customersAssignedCount: number;
  }[];
}) => {
  const navigate = useNavigate();
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState("");
  const [replacementDefaultId, setReplacementDefaultId] = useState<
    number | null
  >(null);
  const availabilityTierDeleteAndReassignMutation =
    useAvailabilityTierDeleteAndReassign(availabilityTierId);
  const setDefaultMutation = useAvailabilityTierSetDefault();

  const availabilityTierListQuery = useAvailabilityTierList(
    {},
    {
      enabled: Boolean(availabilityTierId),
    },
  );

  const handleDeleteAndReassign = (
    data: AvailabilityTierDeleteAndReassignFormValues,
  ) => {
    const deleteAndReassign = () =>
      availabilityTierDeleteAndReassignMutation.mutate(
        {
          customerZones: data.customerZones.map((customerZone) => ({
            id: customerZone.customerZoneId,
            newAvailabilityTierId: customerZone.newAvailabilityTierId,
          })),
          directlyAssignedCustomers:
            data.newAvailabilityTierIdForDirectlyAssignedCustomers
              ? {
                  oldAvailabilityTierId: availabilityTierId,
                  newAvailabilityTierId:
                    data.newAvailabilityTierIdForDirectlyAssignedCustomers,
                }
              : null,
        },
        {
          onSuccess: () => {
            setActive(false);
            navigate(ROUTES.AVAILABILITY_TIERS);
          },
          onError: (error) => {
            const message =
              apiV2AssertError(error)?.response?.data.message ?? "";
            setSubmissionErrorMessage(message);
          },
        },
      );

    if (isDefault) {
      if (!replacementDefaultId) return;

      setDefaultMutation.mutate(
        { id: replacementDefaultId },
        {
          onSuccess: deleteAndReassign,
          onError: (error) => {
            const message =
              apiV2AssertError(error)?.response?.data.message ?? "";
            setSubmissionErrorMessage(message);
          },
        },
      );
      return;
    }

    deleteAndReassign();
  };

  const availabilityTierList = availabilityTierListQuery.data?.pages[0] ?? [];

  const isSubmitting =
    availabilityTierDeleteAndReassignMutation.isPending ||
    setDefaultMutation.isPending;
  const filteredAvailabilityTierList = availabilityTierList.filter(
    (tier) => tier.id !== availabilityTierId,
  );
  const replacementDefaultMissing = isDefault && !replacementDefaultId;

  return (
    <div className="flex flex-col gap-4">
      {isDefault && (
        <div className="flex flex-col gap-2">
          <InputLabel>Select a new default product catalogue</InputLabel>
          <InputContainer>
            <InputSelect
              value={replacementDefaultId ?? ""}
              onChange={(event) =>
                setReplacementDefaultId(
                  event.target.value ? Number(event.target.value) : null,
                )
              }
            >
              <option value="">Select a product catalogue…</option>
              {filteredAvailabilityTierList.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </InputSelect>
          </InputContainer>
        </div>
      )}
      {hasEntitiesToReassign ? (
        <AvailabilityTierDeleteAndReassignForm
          customerZonesAssigned={customerZonesAssigned}
          customersDirectlyAssignedCount={customersDirectlyAssignedCount}
          availabilityTierList={availabilityTierList}
          currentAvailabilityTierId={availabilityTierId}
          isSubmitting={isSubmitting}
          replacementDefaultMissing={replacementDefaultMissing}
          errorMessage={submissionErrorMessage}
          onSubmit={handleDeleteAndReassign}
        />
      ) : (
        <div className="flex justify-end">
          <Button
            color="red"
            disabled={isSubmitting || replacementDefaultMissing}
            loading={isSubmitting}
            onClick={() => {
              handleDeleteAndReassign({
                customerZones: [],
                newAvailabilityTierIdForDirectlyAssignedCustomers: null,
              });
            }}
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};

type AvailabilityTierDeleteAndReassignFormProps = {
  availabilityTierList: AvailabilityTierListResponseType;
  currentAvailabilityTierId: number;
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: AvailabilityTierGetStatsResponseType["customerZonesAssigned"];
  isSubmitting: boolean;
  replacementDefaultMissing: boolean;
  errorMessage: string;
  onSubmit: (data: AvailabilityTierDeleteAndReassignFormValues) => void;
};

type AvailabilityTierDeleteAndReassignFormValues = {
  customerZones: Array<{
    customerZoneId: number;
    newAvailabilityTierId: number;
  }>;
  newAvailabilityTierIdForDirectlyAssignedCustomers: number | null;
};

const AvailabilityTierDeleteAndReassignForm = ({
  availabilityTierList,
  currentAvailabilityTierId,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
  isSubmitting,
  replacementDefaultMissing,
  errorMessage,
  onSubmit,
}: AvailabilityTierDeleteAndReassignFormProps) => {
  const form = useForm<AvailabilityTierDeleteAndReassignFormValues>({
    defaultValues: {
      customerZones: [],
    },
  });

  const filteredAvailabilityTierList = availabilityTierList.filter(
    (priceTier) => priceTier.id !== currentAvailabilityTierId,
  );

  const customerZones = useWatch({
    control: form.control,
    name: "customerZones",
  });
  const newAvailabilityTierIdForDirectlyAssignedCustomers = useWatch({
    control: form.control,
    name: "newAvailabilityTierIdForDirectlyAssignedCustomers",
  });

  const noValues =
    (customerZonesAssigned.length > 0 &&
      !customerZones[0]?.newAvailabilityTierId) ||
    !newAvailabilityTierIdForDirectlyAssignedCustomers;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <div className="flex flex-col gap-4">
        {customerZonesAssigned.map((customerZoneAssigned, idx) => {
          const customerZoneName =
            customerZoneAssigned.name ??
            `Customer Zone - ${customerZoneAssigned.id}`;
          return (
            <div key={customerZoneAssigned.id} className="flex flex-col gap-2">
              <InputLabel>{`Select product catalogue for your '${customerZoneName}' customer zone`}</InputLabel>
              <InputContainer>
                <InputSelect
                  {...form.register(`customerZones.${idx}`, {
                    required: true,
                    setValueAs: (value) => ({
                      customerZoneId: customerZoneAssigned.id,
                      newAvailabilityTierId: Number(value),
                    }),
                  })}
                >
                  <option value="">Select a product catalogue…</option>
                  {filteredAvailabilityTierList.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name}
                    </option>
                  ))}
                </InputSelect>
              </InputContainer>
            </div>
          );
        })}
        <div className="flex flex-col gap-2">
          <InputLabel>{`Select product catalogue for ${customersDirectlyAssignedCount} individually assigned customers`}</InputLabel>
          <InputContainer>
            <InputSelect
              {...form.register(
                "newAvailabilityTierIdForDirectlyAssignedCustomers",
                {
                  required: true,
                  setValueAs: (value) => Number(value),
                },
              )}
            >
              <option value="">Select a product catalogue…</option>
              {filteredAvailabilityTierList.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </InputSelect>
          </InputContainer>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          disabled={isSubmitting || noValues || replacementDefaultMissing}
          loading={isSubmitting}
          type="submit"
        >
          Delete and Assign
        </Button>
      </div>
    </form>
  );
};
